import { useState, useEffect } from 'react'
import { useAuth } from '../../hooks/useAuth'
import api from '../../services/api.js'
import { copyText } from '../../utils/clipboard'
import { formatDate } from '../../utils/helpers'
import { useTranslation } from 'react-i18next'

const CATEGORIES = ['Contracts', 'Complaints', 'Invoices', 'Other']
const DOC_CATEGORIES = ['CVs', 'IDs', 'Contracts', 'Invoices', 'Deliverables', 'Certifications', 'Feedback', 'TORs']

export default function GiveAccess() {
  const { t } = useTranslation('ops')
  const { t: tc } = useTranslation('common')
  const { user } = useAuth()
  const isAdmin = user?.role === 'Admin'

  const [grants, setGrants] = useState([])
  const [msg, setMsg] = useState(null)
  const [folders, setFolders] = useState({})          // {category: drive_url}
  const [folderDraft, setFolderDraft] = useState({})  // edits in progress

  // Admin grant form
  const [form, setForm] = useState({ category: 'Contracts', label: '', drive_url: '', granted_to_email: '', note: '' })
  const [sharing, setSharing] = useState(false)

  // Non-admin request
  const [reqCategory, setReqCategory] = useState('Contracts')
  const [reqReason, setReqReason] = useState('')
  const [requesting, setRequesting] = useState(false)
  const [requested, setRequested] = useState(false)

  // Admin: secure document store (GCS) browse + share-on-request
  const [docCategory, setDocCategory] = useState('CVs')
  const [docSearch, setDocSearch] = useState('')
  const [docFiles, setDocFiles] = useState([])
  const [docLoading, setDocLoading] = useState(false)
  const [docError, setDocError] = useState(null)
  const [copiedKey, setCopiedKey] = useState(null)

  const loadFiles = async (cat) => {
    setDocLoading(true); setDocError(null); setDocFiles([])
    try {
      const r = await api.get('/admin/files', { params: { category: cat } })
      setDocFiles(r.data.files || [])
    } catch (e) {
      setDocError(e.response?.data?.detail || 'Could not load documents')
    } finally { setDocLoading(false) }
  }

  const shareFile = async (key) => {
    try {
      const r = await api.post('/admin/files/share-link', { key, expires_hours: 24 })
      const ok = await copyText(r.data.url)
      setCopiedKey(key)
      setTimeout(() => setCopiedKey(null), 2500)
      setMsg({ type: 'success', text: ok ? `Secure link copied (valid 24h) — paste it to the requester.` : `Link created (valid 24h): ${r.data.url}` })
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.detail || 'Could not create share link' })
    }
  }

  const fmtSize = (b) => b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : b >= 1024 ? `${(b / 1024).toFixed(0)} KB` : `${b} B`

  const load = () => api.get('/legal/access-grants').then(r => setGrants(r.data || [])).catch(() => {})
  useEffect(() => {
    load()
    if (isAdmin) {
      api.get('/legal/folders').then(r => {
        const m = {}; (r.data || []).forEach(f => { m[f.category] = f.drive_url })
        setFolders(m)
      }).catch(() => {})
      loadFiles('CVs')
    }
  }, [isAdmin])

  const saveFolder = async (cat) => {
    const url = (folderDraft[cat] ?? folders[cat] ?? '').trim()
    if (!url) return
    try {
      await api.put('/legal/folders', { category: cat, drive_url: url })
      setFolders(f => ({ ...f, [cat]: url }))
      setMsg({ type: 'success', text: `${cat} folder link saved` })
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.detail || 'Could not save folder link' })
    }
  }

  const share = async (e) => {
    e.preventDefault()
    if (sharing) return
    if (!form.drive_url.trim() || !form.granted_to_email.trim()) { setMsg({ type: 'error', text: 'Drive link and recipient email are required.' }); return }
    setSharing(true); setMsg(null)
    try {
      await api.post('/legal/access-grants', form)
      setMsg({ type: 'success', text: `Access shared with ${form.granted_to_email}.` })
      setForm({ category: 'Contracts', label: '', drive_url: '', granted_to_email: '', note: '' })
      load()
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.detail || 'Could not share access' })
    } finally { setSharing(false) }
  }

  const requestAccess = async () => {
    if (requesting) return
    setRequesting(true); setMsg(null)
    try {
      await api.post('/legal/access-requests', { category: reqCategory, reason: reqReason })
      setRequested(true)
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.detail || 'Could not send request' })
    } finally { setRequesting(false) }
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">{t('give_access')}</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          {isAdmin
            ? 'Share Contracts, Complaints or Invoices (Google Drive links) with specific people.'
            : 'Documents shared with you appear here. Need something else? Request access from the Admin.'}
        </p>
      </div>

      {msg && <div className={`text-sm px-3 py-2 rounded-lg ${msg.type === 'error' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'}`}>{msg.text}</div>}

      {/* Admin: secure document store — browse files + share a time-limited link on request */}
      {isAdmin && (
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="font-semibold text-gray-800">{t('secure_documents')}</h3>
          <p className="text-xs text-gray-500 mt-0.5 mb-4">
            All uploaded files are stored securely in the cloud. Pick a category, then generate a
            time-limited secure link (valid 24h) to share a specific file when someone requests it.
          </p>
          <div className="flex flex-wrap gap-2 mb-3">
            {DOC_CATEGORIES.map(c => (
              <button
                key={c}
                onClick={() => { setDocCategory(c); setDocSearch(''); loadFiles(c) }}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium ${docCategory === c ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                {c}
              </button>
            ))}
          </div>

          {/* Search within the selected category */}
          <div className="relative mb-4">
            <svg className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
            <input
              value={docSearch}
              onChange={e => setDocSearch(e.target.value)}
              placeholder={`Search ${docCategory} by file name or ID (e.g. user_42, 3P-CON-…)`}
              className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {(() => {
            const q = docSearch.trim().toLowerCase()
            const shown = q ? docFiles.filter(f => (f.name || '').toLowerCase().includes(q) || (f.key || '').toLowerCase().includes(q)) : docFiles
            return docLoading ? (
            <p className="text-sm text-gray-400 py-4 text-center">{t('loading')}</p>
          ) : docError ? (
            <p className="text-sm text-amber-600">{docError}</p>
          ) : docFiles.length === 0 ? (
            <p className="text-sm text-gray-400 py-4 text-center">No {docCategory} documents stored yet.</p>
          ) : shown.length === 0 ? (
            <p className="text-sm text-gray-400 py-4 text-center">No files match “{docSearch}”.</p>
          ) : (
            <div className="border border-gray-100 rounded-lg divide-y divide-gray-100 max-h-80 overflow-y-auto">
              {shown.map(f => (
                <div key={f.key} className="flex items-center justify-between gap-3 px-3 py-2">
                  <div className="min-w-0">
                    <p className="text-sm text-gray-800 truncate">{f.name}</p>
                    <p className="text-[11px] text-gray-400">{fmtSize(f.size)}{f.last_modified ? ` · ${formatDate(f.last_modified)}` : ''}</p>
                  </div>
                  <button
                    onClick={() => shareFile(f.key)}
                    className={`shrink-0 text-xs px-3 py-1.5 rounded-md border ${copiedKey === f.key ? 'bg-green-50 text-green-700 border-green-200' : 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100'}`}
                  >
                    {copiedKey === f.key ? '✓ Link copied' : '🔗 Copy secure link'}
                  </button>
                </div>
              ))}
            </div>
          )
          })()}
        </div>
      )}

      {/* Admin: master Drive folders — quick-open to find a file, then share it below */}
      {isAdmin && (
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="font-semibold text-gray-800 mb-1">{t('master_drive_folders')}</h3>
          <p className="text-xs text-gray-500 mb-4">{t('save_drive_link')} <b>{t('open')}</b> to jump into Drive, find the file you need, then share its link in the form below.</p>
          <div className="space-y-3">
            {CATEGORIES.map(cat => (
              <div key={cat} className="flex flex-col sm:flex-row sm:items-center gap-2">
                <span className="w-28 text-sm font-medium text-gray-700 shrink-0">{cat}</span>
                <input
                  value={folderDraft[cat] ?? folders[cat] ?? ''}
                  onChange={e => setFolderDraft(d => ({ ...d, [cat]: e.target.value }))}
                  placeholder="https://drive.google.com/drive/folders/..."
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => saveFolder(cat)} className="px-3 py-2 text-xs font-medium bg-gray-700 text-white rounded-lg hover:bg-gray-800">{tc('actions.save')}</button>
                  {folders[cat] && (
                    <a href={folders[cat]} target="_blank" rel="noopener noreferrer" className="px-3 py-2 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100">{t('open_ext')}</a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Admin: share a Drive link */}
      {isAdmin && (
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="font-semibold text-gray-800 mb-4">{t('share_doc_folder')}</h3>
          <form onSubmit={share} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{tc('labels.category')}</label>
              <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t('label_optional')}</label>
              <input value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} placeholder={t('ph_folder_label')} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">{t('gdrive_link')}</label>
              <input value={form.drive_url} onChange={e => setForm({ ...form, drive_url: e.target.value })} placeholder="https://drive.google.com/..." className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t('recipient_email')}</label>
              <input type="email" value={form.granted_to_email} onChange={e => setForm({ ...form, granted_to_email: e.target.value })} placeholder="person@example.com" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t('note_optional')}</label>
              <input value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div className="sm:col-span-2">
              <button type="submit" disabled={sharing} className="px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {sharing ? 'Sharing…' : '🔑 Share Access'}
              </button>
              <span className="text-xs text-gray-400 ml-3">{t('make_sure_shared')}</span>
            </div>
          </form>
        </div>
      )}

      {/* Non-admin: request access */}
      {!isAdmin && (
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          {requested ? (
            <div className="text-center py-4">
              <div className="text-4xl mb-2">🔐</div>
              <p className="text-sm text-gray-700">{t('request_sent_admin')}</p>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t('what_access')}</label>
                <select value={reqCategory} onChange={e => setReqCategory(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <input value={reqReason} onChange={e => setReqReason(e.target.value)} placeholder={t('ph_reason_optional')} className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              <button onClick={requestAccess} disabled={requesting} className="px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {requesting ? 'Sending…' : 'Request Access'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Shared items list */}
      <div className="bg-white border border-gray-200 rounded-xl">
        <div className="p-5 border-b border-gray-200">
          <h3 className="font-semibold text-gray-800">{isAdmin ? 'All Shared Items' : 'Shared With You'}</h3>
        </div>
        {grants.length === 0 ? (
          <p className="p-6 text-sm text-gray-400 text-center">{t('nothing_shared')}</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {grants.map(g => (
              <div key={g.id} className="p-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">{g.category}</span>
                    <span className="text-sm font-medium text-gray-900 truncate">{g.label || 'Document'}</span>
                  </div>
                  {isAdmin && <p className="text-xs text-gray-500 mt-0.5">Shared with {g.granted_to_email}</p>}
                  {g.note && <p className="text-xs text-gray-400 mt-0.5">{g.note}</p>}
                </div>
                <a href={g.drive_url} target="_blank" rel="noopener noreferrer" className="shrink-0 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100">
                  Open ↗
                </a>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
